import axios from "axios";
import { ensureCsrfToken } from "../lib/csrf";
export const PUBLISH_PORTFOLIO_SNAPSHOT_URL = "/api/digital-risk/dashboard/publish-portfolio-snapshot/";

export async function publishPortfolioSnapshot(payload, canSend = () => true, { waitUntilReady, onSending } = {}) {
  let csrfToken;
  try { csrfToken = await ensureCsrfToken(); }
  catch (cause) {
    const error = new Error('CSRF validation could not be prepared. No publication was sent.');
    error.notSent = true; throw error;
  }
  while (!canSend()) {
    if (waitUntilReady && await waitUntilReady()) continue;
    const error = new Error('Session validation is pending or authentication is unavailable. No publication was sent.');
    error.notSent = true;
    throw error;
  }
  try {
    // No asynchronous work between this boundary and axios.post. Beyond here,
    // transport failures are ambiguous and must use the original receipt.
    onSending?.();
    const { data } = await axios.post(PUBLISH_PORTFOLIO_SNAPSHOT_URL, payload, {
      withCredentials: true, headers: { "X-CSRFToken": csrfToken }, timeout: 120000,
    });
    if (data?.operation_id && ['PROCESSING', 'RECOVER'].includes(data.operation_status)) return data;
    if (payload.operation_id && (data?.operation_id !== payload.operation_id || data?.operation_status !== 'SUCCESS' || !data?.id || !data?.analytical_evidence_id)) {
      throw new Error('Publication response could not be verified.');
    }
    return data;
  } catch (error) {
    const failure = new Error(error?.response?.data?.detail || 'Publication outcome requires verification.');
    failure.status = error?.response?.status;
    failure.rejected = error?.response?.data?.request_rejected === true;
    failure.confirmedFailure = error?.response?.data?.operation_status === 'CONFIRMED_FAILURE';
    throw failure;
  }
}

export async function readPublicationOperation(id, program) {
  const { data } = await axios.get(`/api/digital-risk/dashboard/program-evidence-operations/${encodeURIComponent(id)}/`, {
    withCredentials: true, params: { program }, timeout: 10000,
  });
  if (data?.operation_id !== id) throw new Error('Unverified operation response.');
  return data;
}

export async function discoverPublicationOperation(program) {
  const { data } = await axios.get('/api/digital-risk/dashboard/program-evidence-operations/', {
    withCredentials: true, params: { program }, timeout: 10000,
  });
  if (data?.operation_status !== 'NONE' && !data?.operation_id) throw new Error('Unverified discovery response.');
  return data;
}

export async function acknowledgePublicationOperation(id, program, canSend) {
  const csrfToken = await ensureCsrfToken();
  if (!canSend()) throw new Error('Waiting for verified session validity. No acknowledgement was sent.');
  const { data } = await axios.post(`/api/digital-risk/dashboard/program-evidence-operations/${encodeURIComponent(id)}/acknowledge/`,
    { program }, { withCredentials: true, headers: { 'X-CSRFToken': csrfToken }, timeout: 10000 });
  if (data?.operation_id !== id) throw new Error('Unverified acknowledgement response.');
  return data;
}

export async function recoverPublicationOperation(id, program, attempt, canSend) {
  let csrfToken;
  try { csrfToken = await ensureCsrfToken(); }
  catch { throw Object.assign(new Error('Recovery was not sent. CSRF preparation failed.'), { notSent: true }); }
  if (!canSend()) throw Object.assign(new Error('Recovery was not sent. Verified session validity is required.'), { notSent: true });
  const { data } = await axios.post(`/api/digital-risk/dashboard/program-evidence-operations/${encodeURIComponent(id)}/recover/`,
    { program, execution_attempt_id: attempt },
    { withCredentials: true, headers: { 'X-CSRFToken': csrfToken }, timeout: 120000 });
  if (data?.operation_id !== id) throw new Error('Unverified recovery response.');
  return data;
}
